/**
 * Utility that executes the actual database import for the matrix import wizard.
 * Inserts users, company members, space members, qualifications, and assignments
 * into Supabase.
 */

import { supabase } from '@/lib/supabase';

/**
 * Executes the full matrix import into Supabase.
 *
 * Order of operations:
 *   1. Create new user profiles + company memberships via bulk_import_company_users RPC
 *   2. Resolve all user IDs (created + matched) and add to space
 *   3. Create new qualifications (action: 'create_from_course' | 'create_custom')
 *   4. Upsert compliance assignments
 *
 * The import aborts early if critical phases fail entirely (e.g. all users fail).
 *
 * @param {Object} params
 * @param {Array} params.users
 * @param {Array} params.qualifications
 * @param {Array} params.assignments
 * @param {string} params.projectId
 * @param {string} params.companyId
 * @param {string} params.userProfileId
 * @param {function(string, number, number, string): void} params.onProgress
 * @returns {Promise<{ success: boolean, results: object }>}
 */
export async function executeImport({
  users,
  qualifications,
  assignments,
  projectId,
  companyId,
  userProfileId,
  onProgress,
}) {
  const results = {
    usersCreated: 0,
    usersMatched: 0,
    usersSkipped: 0,
    qualsCreated: 0,
    qualsExisting: 0,
    qualsSkipped: 0,
    assignmentsCreated: 0,
    assignmentsFailed: 0,
    errors: [],
  };

  // Maps for linking row/col indices to database IDs
  const rowIndexToUserId = new Map();
  const colIndexToRequirementId = new Map();

  const usersToCreate = users.filter((u) => u.action === 'create_new');
  const usersToMatch = users.filter((u) => u.action === 'match');
  const usersToSkip = users.filter((u) => u.action === 'skip');
  results.usersSkipped = usersToSkip.length;

  // -------------------------------------------------------------------------
  // Phase 1: Create new user profiles via RPC
  // -------------------------------------------------------------------------
  if (usersToCreate.length > 0) {
    onProgress('users', 0, 1, `Creating ${usersToCreate.length} new users...`);

    const rpcPayload = usersToCreate.map((u) => ({
      name: `${u.firstName || ''} ${u.lastName || ''}`.trim(),
      email: u.email || null,
      role: 'User',
    }));

    try {
      const { data: rpcResult, error: rpcError } = await supabase.rpc(
        'bulk_import_company_users',
        {
          p_company_id: companyId,
          p_users: rpcPayload,
        }
      );

      if (rpcError) throw rpcError;

      results.usersCreated = rpcResult?.added || 0;
      const rpcErrors = rpcResult?.errors || [];
      for (const err of rpcErrors) {
        results.errors.push({
          phase: 'users',
          detail: `Failed to create user "${err.email || 'unknown'}"`,
          error: err.error || String(err),
        });
      }

      onProgress('users', 1, 1, `Created ${results.usersCreated} users`);
    } catch (err) {
      results.errors.push({
        phase: 'users',
        detail: `Bulk user creation failed entirely`,
        error: err.message || String(err),
      });

      // If we can't create any users AND there are no matched users, abort
      if (usersToMatch.length === 0) {
        return { success: false, results };
      }
    }

    // Look up the created profile IDs so we can map row indices
    onProgress('users', 1, 1, 'Resolving created user IDs...');
    for (const user of usersToCreate) {
      try {
        // Try email lookup first, fall back to name
        let profile = null;
        if (user.email) {
          const { data } = await supabase
            .from('user_profiles')
            .select('id')
            .eq('company_id', companyId)
            .ilike('email', user.email)
            .maybeSingle();
          profile = data;
        }
        if (!profile && user.firstName) {
          const { data } = await supabase
            .from('user_profiles')
            .select('id')
            .eq('company_id', companyId)
            .ilike('first_name', user.firstName)
            .ilike('last_name', user.lastName || '')
            .maybeSingle();
          profile = data;
        }
        if (profile?.id) {
          rowIndexToUserId.set(user.rowIndex, profile.id);
        }
      } catch {
        // Non-critical — the user just won't get assignments
      }
    }
  }

  // Populate the map for matched users
  for (const user of usersToMatch) {
    if (user.matchedProfile?.id) {
      rowIndexToUserId.set(user.rowIndex, user.matchedProfile.id);
      results.usersMatched++;
    }
  }

  // If we have zero resolved users, abort — nothing useful can happen
  if (rowIndexToUserId.size === 0) {
    results.errors.push({
      phase: 'users',
      detail: 'No users could be created or matched — aborting import',
      error: 'No valid user IDs to assign to',
    });
    return { success: false, results };
  }

  // -------------------------------------------------------------------------
  // Phase 2: Ensure all resolved users are in the space
  // -------------------------------------------------------------------------
  const allUserIds = [...rowIndexToUserId.values()];
  onProgress('users_space', 0, allUserIds.length, 'Adding users to space...');

  for (let i = 0; i < allUserIds.length; i++) {
    const userId = allUserIds[i];
    onProgress('users_space', i + 1, allUserIds.length, `Checking space membership ${i + 1}/${allUserIds.length}`);

    try {
      const { data: existing } = await supabase
        .from('space_members')
        .select('id')
        .eq('project_id', projectId)
        .eq('user_id', userId)
        .maybeSingle();

      if (!existing) {
        const { error: insertError } = await supabase
          .from('space_members')
          .insert({
            project_id: projectId,
            user_id: userId,
            show_on_matrix: true,
            status: 'active',
            created_by: userProfileId,
          });

        if (insertError) throw insertError;
      }
    } catch (err) {
      results.errors.push({
        phase: 'users_space',
        detail: `Failed to add user to space`,
        error: err.message || String(err),
      });
    }
  }

  // -------------------------------------------------------------------------
  // Phase 3: Create new qualifications
  // -------------------------------------------------------------------------
  const qualsToCreate = qualifications.filter(
    (q) => q.action === 'create_from_course' || q.action === 'create_custom'
  );
  const qualsExisting = qualifications.filter((q) => q.action === 'use_existing');
  const qualsToSkip = qualifications.filter((q) => q.action === 'skip');

  results.qualsSkipped = qualsToSkip.length;
  results.qualsExisting = qualsExisting.length;

  // Populate the map for existing qualifications
  for (const qual of qualsExisting) {
    if (qual.matchedRequirement?.id) {
      colIndexToRequirementId.set(qual.colIndex, qual.matchedRequirement.id);
    }
  }

  onProgress('qualifications', 0, qualsToCreate.length, 'Creating qualifications...');

  for (let i = 0; i < qualsToCreate.length; i++) {
    const qual = qualsToCreate[i];
    onProgress('qualifications', i + 1, qualsToCreate.length, `Creating "${qual.rawName}"`);

    try {
      // For course-linked quals, use the matched course name from the database.
      // Only custom quals keep the raw spreadsheet name.
      const qualName = qual.action === 'create_from_course' && qual.selectedCourse?.label
        ? qual.selectedCourse.label
        : qual.rawName;

      const insertData = {
        project_id: projectId,
        name: qualName,
        show_on_matrix: true,
        display_order: qual.colIndex,
        requirement_type: 'course',
        status: 'active',
        approval_status: 'approved',
        created_by: userProfileId,
      };

      if (qual.action === 'create_from_course' && qual.selectedCourse) {
        insertData.course_link_id = qual.selectedCourse?.data?.id?.toString() || null;
        insertData.course_link_type =
          qual.selectedCourse?.data?._isVerifiedCourse ? 'verified' : 'custom';
      } else if (qual.action === 'create_custom') {
        // Create a custom_courses entry so the requirement has a proper course link
        const { data: customCourse, error: customCourseError } = await supabase
          .from('custom_courses')
          .insert({ title: qualName, company_id: companyId })
          .select('id')
          .single();

        if (customCourseError) {
          console.error('Failed to create custom course for:', qualName, customCourseError);
        } else if (customCourse?.id) {
          insertData.course_link_id = customCourse.id.toString();
          insertData.course_link_type = 'custom';
        }
      }

      const { data: newReq, error: reqError } = await supabase
        .from('compliance_requirements')
        .insert(insertData)
        .select('id')
        .single();

      if (reqError) throw reqError;

      colIndexToRequirementId.set(qual.colIndex, newReq.id);
      results.qualsCreated++;
    } catch (err) {
      results.errors.push({
        phase: 'qualifications',
        detail: `Failed to create qualification "${qual.rawName}"`,
        error: err.message || String(err),
      });
    }
  }

  // If we have zero qualifications resolved, abort
  if (colIndexToRequirementId.size === 0) {
    results.errors.push({
      phase: 'qualifications',
      detail: 'No qualifications could be created or matched — skipping assignments',
      error: 'No valid requirement IDs to assign',
    });
    return { success: false, results };
  }

  // -------------------------------------------------------------------------
  // Phase 4: Create assignments
  // -------------------------------------------------------------------------
  const validAssignments = assignments.filter(
    (a) => rowIndexToUserId.has(a.rowIndex) && colIndexToRequirementId.has(a.colIndex)
  );

  onProgress('assignments', 0, validAssignments.length, 'Creating assignments...');

  for (let i = 0; i < validAssignments.length; i++) {
    const assignment = validAssignments[i];
    const userId = rowIndexToUserId.get(assignment.rowIndex);
    const requirementId = colIndexToRequirementId.get(assignment.colIndex);

    onProgress('assignments', i + 1, validAssignments.length, `Assignment ${i + 1} of ${validAssignments.length}`);

    try {
      const { error: upsertError } = await supabase
        .from('compliance_assignments')
        .upsert(
          {
            project_id: projectId,
            requirement_id: requirementId,
            team_member_id: userId,
            assigned: true,
            status: assignment.status,
            completed_date: assignment.completedDate || null,
            expiry_date: assignment.expiryDate || null,
            certificate_number: assignment.certificateNumber || null,
          },
          { onConflict: 'project_id,requirement_id,team_member_id' }
        );

      if (upsertError) throw upsertError;

      results.assignmentsCreated++;
    } catch (err) {
      results.assignmentsFailed++;
      results.errors.push({
        phase: 'assignments',
        detail: `Failed to upsert assignment (row ${assignment.rowIndex}, col ${assignment.colIndex})`,
        error: err.message || String(err),
      });
    }
  }

  return {
    success: results.errors.length === 0,
    results,
  };
}
